import moment from 'moment';

import { LeagueGameDayHeaderDetails } from '@/modules/league/new-components/types';

// function for formating time objects into readable strings
export const formatTime = (timeObj: {
  months: number;
  days: number;
  hours: number;
  minutes: number;
}) =>
  ` 
${timeObj?.months > 0 ? (timeObj?.months > 1 ? `${timeObj?.months} months` : `${timeObj?.months} month`) : ''} 
${timeObj?.days > 0 ? (timeObj?.days > 1 ? `${timeObj?.days} days` : `${timeObj?.days} day`) : ''} 
${timeObj?.hours > 0 ? (timeObj?.hours > 1 ? `${timeObj?.hours} hours` : `${timeObj?.hours} hour`) : ''} 
${timeObj?.minutes > 1 ? `${timeObj?.minutes} minutes` : `${timeObj?.minutes} minute`} `;

// all messages for flex league
export const getFlexLeagueMessage = (
  gameDayInfo: LeagueGameDayHeaderDetails | undefined,
  scoreDeadline: moment.Moment | null
) => {
  if (gameDayInfo?.isBeforeOptIn === true) {
    return `Time remaining ${formatTime(gameDayInfo.timeUntilOptIn)}`;
  }
  if (gameDayInfo?.isOptInActive === true && !gameDayInfo.isLocked) {
    return `Opt-in ends in ${formatTime(gameDayInfo.timeUntilOptInEnd)}`;
  }
  if (gameDayInfo?.isAfterOptIn === true) {
    return `Opt-in passed ${formatTime(gameDayInfo.timeSinceOptInEnd)} ago`;
  }
  if (gameDayInfo?.isLocked) {
    return `Enter score ends in ${scoreDeadline?.days()} days ${scoreDeadline?.hours()} hours ${scoreDeadline?.minutes()} mins`;
  }
  return '';
};

// All messages for regular league
export const getRegularLeagueMessage = (gameDayInfo: LeagueGameDayHeaderDetails | undefined) => {
  if (gameDayInfo?.isBeforeOptIn && !gameDayInfo.isLocked) {
    return `Opens in ${formatTime(gameDayInfo.timeUntilOptIn)}`;
  }
  if (gameDayInfo?.isOptInActive && !gameDayInfo.isLocked) {
    return `Time remaining ${formatTime(gameDayInfo.timeUntilOptInEnd)}`;
  }
  if (gameDayInfo?.isAfterOptIn && !gameDayInfo.isLocked) {
    return `Opt-in passed ${formatTime(gameDayInfo.timeSinceOptInEnd)} ago`;
  }
  if (gameDayInfo?.isLocked && !gameDayInfo.isMatchStarted) {
    return 'Groups are generated';
  }
  if (gameDayInfo?.DateLockScores === null && gameDayInfo?.NumberOfFutureMatchDatesStarted === 0) {
    return 'Game day in progress';
  }
  if (gameDayInfo?.isMatchStarted && gameDayInfo.TotalGamesRemaining === 0) {
    return 'Game day completed';
  }
  if (gameDayInfo?.DateLockScores !== null && gameDayInfo?.TotalGamesRemaining === 0) {
    return 'Game day is closed';
  }
  if (
    gameDayInfo?.NumberOfFutureMatchDatesStarted &&
    gameDayInfo?.NumberOfFutureMatchDatesStarted > 0 &&
    !gameDayInfo?.DateLockScores
  ) {
    return 'Game day CANNOT complete. Future game days have started or completed.';
  }
  return '';
};

// Display appropriate message based on whether it's a flex or regular league
export const renderTimeMessage = (
  isFlexLeague: boolean | undefined,
  gameDayInfo: LeagueGameDayHeaderDetails | undefined
) => {
  const scoreDeadline = gameDayInfo?.scoreDeadline ? moment(gameDayInfo.scoreDeadline) : null;
  return isFlexLeague
    ? getFlexLeagueMessage(gameDayInfo, scoreDeadline)
    : getRegularLeagueMessage(gameDayInfo);
};

export const formatDateWithTimezone = (
  date: Date | string | undefined,
  timezone: string | undefined
) => {
  if (!date || !timezone) return '';
  return (
    date &&
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(new Date(date))
      .find((part) => part.type === 'timeZoneName')?.value
  );
};

// related messages for both flex and regular leagues
export const renderDateMessage = (
  isFlexLeague: boolean | undefined,
  gameDayInfo: LeagueGameDayHeaderDetails | undefined
) => {
  if (!gameDayInfo) return '';
  const scoreDeadline = gameDayInfo?.scoreDeadline
    ? moment.parseZone(gameDayInfo.scoreDeadline)
    : null;
  const optInFromDate = gameDayInfo?.optInFromDate
    ? moment.parseZone(gameDayInfo.optInFromDate)
    : null;
  const optInToDate = gameDayInfo?.optInToDate ? moment.parseZone(gameDayInfo.optInToDate) : null;
  const timezoneAbbreviationForScoreDeadline = formatDateWithTimezone(
    gameDayInfo?.scoreDeadline,
    gameDayInfo?.Timezone_IanaID
  );
  const timezoneAbbreviationForOptInFrom = formatDateWithTimezone(
    gameDayInfo?.optInFromDate,
    gameDayInfo?.Timezone_IanaID
  );
  const timezoneAbbreviationForOptInTo = formatDateWithTimezone(
    gameDayInfo?.optInToDate,
    gameDayInfo?.Timezone_IanaID
  );

  if (isFlexLeague && gameDayInfo?.isAfterOptIn) {
    return `Scores are due on ${scoreDeadline?.format('MMM DD, YYYY [at] h:mm A')} ${timezoneAbbreviationForScoreDeadline}`;
  }
  if (gameDayInfo?.isBeforeOptIn && !gameDayInfo.isLocked) {
    return `Opt-in begins on ${optInFromDate?.format('ddd, MMM DD [at] h:mm A')} ${timezoneAbbreviationForOptInFrom}`;
  }
  if (gameDayInfo?.isOptInActive && !gameDayInfo.isLocked) {
    return `Opt in deadline on ${optInToDate?.format('ddd, MMM DD [at] h:mm A')} ${timezoneAbbreviationForOptInTo}`;
  }
  if (gameDayInfo?.isLocked && !gameDayInfo.isMatchStarted) {
    return 'Opt in is locked';
  }
  return '';
};

//api call
export const getTimeDifference = (startDate: moment.Moment, endDate: moment.Moment) => {
  const duration = moment.duration(endDate.diff(startDate));
  return {
    months: duration.months(),
    days: duration.days(),
    hours: duration.hours(),
    minutes: duration.minutes(),
  };
};
